import { LocaleTypeEnum } from "@/types/localization";
import { normalizeToSupportedLang } from "./normalizeToSupportedLang";

/**
 * Infers the UI language from the browser/device preferences exposed via
 * `navigator.languages` (falling back to `navigator.language`).
 *
 * Walks the preference list in order and returns the first entry whose primary
 * subtag is supported, so a user whose list is ["pl", "en", "cs"] gets EN.
 * Returns `null` when none of the preferred languages are supported (or the API
 * is unavailable), letting the caller fall through to the default locale.
 *
 * Works identically in browser-embedded and native WebView modes: both Android
 * WebView and iOS WKWebView surface the device locale through this API.
 */
export const detectBrowserLanguage = (): LocaleTypeEnum | null => {
  if (typeof navigator === "undefined") return null;

  const preferred: readonly string[] =
    navigator.languages?.length > 0
      ? navigator.languages
      : [navigator.language].filter(Boolean);

  for (const lang of preferred) {
    const supported = normalizeToSupportedLang(lang);
    if (supported) return supported;
  }

  return null;
};
